/**
 * widgets/module-panels.ts — node-switchable monitoring proužky pro modulové
 * stránky. Sedí NAD management UI (limiter / hostname-DNS) přes <ModulePanel>:
 * rychlý pohled na živé zdraví + přepínač nodu (stejnou stránkou se podíváš na
 * jiný nod flotily). Management pod tím zůstává nedotčený.
 */
import type { WidgetPanel } from './types';
import i18n from '../lib/i18n';

// Resources — živé využití (usage); doplňuje alokační capacity header limiteru.
export const TOOLBOX_RESOURCES: WidgetPanel = {
  id: 'toolbox-resources-mon',
  label: 'Resources',
  node: null,
  widgets: [
    { widgetId: 'system-settings.cpu', format: 'sm' },
    { widgetId: 'system-settings.ram', format: 'sm' },
    { widgetId: 'system-settings.disk', format: 'sm' },
    { widgetId: 'system-settings.uptime', format: 'sm' },
  ],
};

// System — základní glance systému nad hostname/DNS/NTP managementem.
export const TOOLBOX_SYSTEM: WidgetPanel = {
  id: 'toolbox-system-mon',
  label: 'System',
  node: null,
  widgets: [
    { widgetId: 'system-settings.host', format: 'sm' },
    { widgetId: 'system-settings.cpu', format: 'sm' },
    { widgetId: 'system-settings.ram', format: 'sm' },
    { widgetId: 'system-settings.uptime', format: 'sm' },
  ],
};

// GitLab — hlavní záložka: instance (ACM-hostované GitLaby),
// projekty (externí připojené), servery (registrace externích GitLab hostů).
export const GITLAB_MAIN: WidgetPanel = {
  id: 'gitlab-main',
  label: 'GitLab',
  node: null,
  widgets: [
    { widgetId: 'gitlab.instances', format: 'lg' },
    { widgetId: 'gitlab.projects', format: 'lg' },
    { widgetId: 'gitlab.groups', format: 'lg' },
    { widgetId: 'gitlab.users', format: 'lg' },
    { widgetId: 'gitlab.servers', format: 'md' },
  ],
};

// GitLab — workery/runnery (compile workers). Vlastní záložka, protože
// workery jsou orthogonální ke správě GitLab serverů — přiřazují se k nim,
// ale mají vlastní lifecycle a mohou běžet cross-node.
export const GITLAB_WORKERS: WidgetPanel = {
  id: 'gitlab-workers',
  label: i18n.t('gitlab.tabs.workers', 'Workery'),
  node: null,
  widgets: [
    { widgetId: 'gitlab.runners', format: 'lg' },
  ],
};

// GitLab — údržba a nastavení. Vlastní záložka, protože je to stav uzlu
// (kolik zabírají zálohy a image, kolik zbývá místa) plus volby, které ACM
// v GitLabu drží — ne operace nad jednou instancí.
export const GITLAB_MAINTENANCE: WidgetPanel = {
  id: 'gitlab-maintenance',
  label: i18n.t('gitlab.tabs.maintenance', 'Údržba'),
  node: null,
  widgets: [
    { widgetId: 'gitlab.maintenance', format: 'lg' },
  ],
};

// Updates — modulová záložka Updates JAKO widget panel (zamčený default, node-switchable).
// Nahrazuje bývalý bespoke UpdatesTab: sysupdate widget nese CO/JAK se updatuje,
// živý progress i restart prompt. Přehození nodu (NodePicker) = update jiného nodu.
// Oznámení — historie jako plnohodnotná karta (zvoneček zůstává pomíjivý
// rychlý povrch; tady je dohledatelnost: co přišlo, kdy, v jakém stavu).
export const NOTIFICATIONS_HISTORY: WidgetPanel = {
  id: 'notifications-history',
  label: 'Historie',
  node: null,
  widgets: [
    { widgetId: 'notifications.history', format: 'lg' },
  ],
};

export const TOOLBOX_UPDATES: WidgetPanel = {
  id: 'toolbox-updates-mon',
  label: 'Updates',
  node: null,
  widgets: [
    { widgetId: 'toolbox.rollout', format: 'lg' },
    { widgetId: 'toolbox.sysupdate', format: 'lg' },
  ],
};

// Toolbox 2.1 — plné management záložky (stejné na /core/toolbox i node workspace)
export const TOOLBOX_HEALTH: WidgetPanel = {
  id: 'toolbox-health',
  label: 'Health',
  node: null,
  widgets: [{ widgetId: 'toolbox.health', format: 'lg' }],
};
export const TOOLBOX_SECURITY: WidgetPanel = {
  id: 'toolbox-security',
  label: 'Security',
  node: null,
  widgets: [{ widgetId: 'toolbox.security', format: 'lg' }],
};
export const TOOLBOX_BACKUP: WidgetPanel = {
  id: 'toolbox-backup',
  label: 'Backup',
  node: null,
  // Více widgetů = po odemčení lze měnit rozvržení (ne jen 1 monolit).
  widgets: [
    { widgetId: 'toolbox.backup', format: 'lg' },
    { widgetId: 'orchestrator.summary', format: 'md' },
  ],
};
export const TOOLBOX_CLEANER: WidgetPanel = {
  id: 'toolbox-cleaner',
  label: 'Cleaner',
  node: null,
  widgets: [{ widgetId: 'toolbox.cleaner', format: 'lg' }],
};
export const TOOLBOX_WATCHDOG: WidgetPanel = {
  id: 'toolbox-watchdog',
  label: 'Watchdog',
  node: null,
  widgets: [{ widgetId: 'toolbox.watchdog', format: 'lg' }],
};
export const TOOLBOX_CRASH_RECOVERY: WidgetPanel = {
  id: 'toolbox-crash-recovery',
  label: 'Crash recovery',
  node: null,
  widgets: [{ widgetId: 'toolbox.crash-recovery', format: 'lg' }],
};
export const TOOLBOX_RESOURCES_MGMT: WidgetPanel = {
  id: 'toolbox-resources-mgmt',
  label: 'Resources',
  node: null,
  widgets: [{ widgetId: 'toolbox.resources', format: 'lg' }],
};
export const TOOLBOX_SYSTEM_MGMT: WidgetPanel = {
  id: 'toolbox-system-mgmt',
  label: 'System',
  node: null,
  widgets: [{ widgetId: 'toolbox.system', format: 'lg' }],
};
export const TOOLBOX_ACM_LINK: WidgetPanel = {
  id: 'toolbox-acm-link',
  label: 'ACMLink',
  node: null,
  widgets: [{ widgetId: 'toolbox.acm-link', format: 'lg' }],
};

// Network — platformový přehled veřejných URL
export const NETWORK_OVERVIEW: WidgetPanel = {
  id: 'network-overview',
  label: 'Public URLs',
  node: null,
  widgets: [{ widgetId: 'network.overview', format: 'lg' }],
};

// Registry 2.1
export const REGISTRY_TREE: WidgetPanel = {
  id: 'registry-tree',
  label: 'Tree',
  node: null,
  widgets: [{ widgetId: 'registry.tree', format: 'lg' }],
};
export const REGISTRY_HEALTH: WidgetPanel = {
  id: 'registry-health',
  label: 'Health',
  node: null,
  widgets: [{ widgetId: 'registry.health', format: 'lg' }],
};
export const REGISTRY_CATALOG: WidgetPanel = {
  id: 'registry-catalog',
  label: 'Catalog',
  node: null,
  widgets: [{ widgetId: 'registry.catalog', format: 'lg' }],
};
export const REGISTRY_PEERS: WidgetPanel = {
  id: 'registry-peers',
  label: 'Peers',
  node: null,
  widgets: [{ widgetId: 'registry.peers', format: 'lg' }],
};
export const REGISTRY_PUBLISH: WidgetPanel = {
  id: 'registry-publish',
  label: 'Publish',
  node: null,
  widgets: [{ widgetId: 'registry.publish', format: 'lg' }],
};

// Certmgr 2.1
export const CERTMGR_OVERVIEW: WidgetPanel = {
  id: 'certmgr-overview',
  label: 'Overview',
  node: null,
  widgets: [{ widgetId: 'certmgr.overview', format: 'lg' }],
};
export const CERTMGR_CERTIFICATES: WidgetPanel = {
  id: 'certmgr-certificates',
  label: 'Certificates',
  node: null,
  widgets: [{ widgetId: 'certmgr.certificates', format: 'lg' }],
};
export const CERTMGR_STORE: WidgetPanel = {
  id: 'certmgr-store',
  label: 'Store',
  node: null,
  widgets: [{ widgetId: 'certmgr.store', format: 'lg' }],
};
export const CERTMGR_TRUST: WidgetPanel = {
  id: 'certmgr-trust',
  label: 'Trust',
  node: null,
  widgets: [{ widgetId: 'certmgr.trust', format: 'lg' }],
};
export const CERTMGR_LE: WidgetPanel = {
  id: 'certmgr-le',
  label: "Let's Encrypt",
  node: null,
  widgets: [{ widgetId: 'certmgr.le', format: 'lg' }],
};
export const CERTMGR_DNS: WidgetPanel = {
  id: 'certmgr-dns',
  label: 'DNS',
  node: null,
  widgets: [{ widgetId: 'certmgr.dns', format: 'lg' }],
};
export const CERTMGR_DOCKER: WidgetPanel = {
  id: 'certmgr-docker',
  label: 'Root CA',
  node: null,
  widgets: [{ widgetId: 'certmgr.docker', format: 'lg' }],
};

// acm-self — veřejný přístup k control-plane ACM (ne k appkám)
export const ACM_SELF_SERVICES: WidgetPanel = {
  id: 'acm-self-services',
  label: 'Services',
  node: null,
  widgets: [{ widgetId: 'acm-self.services', format: 'lg' }],
};
export const ACM_SELF_ACL: WidgetPanel = {
  id: 'acm-self-acl',
  label: 'ACL',
  node: null,
  widgets: [{ widgetId: 'acm-self.acl', format: 'lg' }],
};
export const ACM_SELF_DOMAINS: WidgetPanel = {
  id: 'acm-self-domains',
  label: 'Domains',
  node: null,
  widgets: [{ widgetId: 'acm-self.domains', format: 'lg' }],
};
export const ACM_SELF_SECURITY: WidgetPanel = {
  id: 'acm-self-security',
  label: 'Security',
  node: null,
  widgets: [{ widgetId: 'acm-self.security', format: 'lg' }],
};

// Codable — widget-first: Projekty | Knihovna | Nastavení.
// Per-projekt (varianta, backend, git) = workspace gear, ne modulové nastavení.
export const CODABLE_PROJECTS: WidgetPanel = {
  id: 'codable-projects',
  label: i18n.t('widgets.registry.codable.projects', 'Projekty'),
  node: null,
  widgets: [{ widgetId: 'codable.projects', format: 'lg' }],
};

export const CODABLE_LIBRARY: WidgetPanel = {
  id: 'codable-library',
  label: i18n.t('codable.tabLibrary', 'Knihovna'),
  node: null,
  widgets: [{ widgetId: 'codable.library', format: 'lg' }],
};

export const CODABLE_SETTINGS: WidgetPanel = {
  id: 'codable-settings',
  label: i18n.t('codableSettings.gearLabel', 'Codable — nastavení'),
  node: null,
  widgets: [{ widgetId: 'codable.settings-hub', format: 'lg' }],
};

// Orchestrator — glance widgets for node workspace / My Panel
export const ORCHESTRATOR_OVERVIEW: WidgetPanel = {
  id: 'orchestrator-overview',
  label: 'Orchestrator',
  node: null,
  widgets: [
    { widgetId: 'orchestrator.summary', format: 'md' },
    { widgetId: 'orchestrator.environments-links', format: 'lg' },
    { widgetId: 'orchestrator.environments', format: 'lg' },
  ],
};

// Docker / Kontejnery 2.1 — widget-first (nahrazuje monolit Containers.tsx).
// Každá záložka = vlastní ModulePanel se zamčeným defaultem + node-switch.
// DNS — widget-first (live zóny, verze, šablony, server, domény)
export const DNS_PROVIDERS: WidgetPanel = {
  id: 'dns-providers',
  label: i18n.t('dns.provideri', 'Provideři'),
  node: null,
  widgets: [{ widgetId: 'dns.providers', format: 'lg' }],
};
export const DNS_ZONES: WidgetPanel = {
  id: 'dns-zones',
  label: i18n.t('dns.zony', 'Zóny'),
  node: null,
  widgets: [{ widgetId: 'dns.zones', format: 'lg' }],
};
export const DNS_TEMPLATES: WidgetPanel = {
  id: 'dns-templates',
  label: i18n.t('widgets.registry.dns.templates', 'Šablony zón'),
  node: null,
  widgets: [{ widgetId: 'dns.templates', format: 'lg' }],
};
export const DNS_SERVER: WidgetPanel = {
  id: 'dns-server',
  label: 'Server',
  node: null,
  widgets: [{ widgetId: 'dns.server', format: 'lg' }],
};
export const DNS_DOMAINS: WidgetPanel = {
  id: 'dns-domains',
  label: i18n.t('widgets.registry.dns.domains', 'Domény tenantů'),
  node: null,
  widgets: [{ widgetId: 'dns.domains', format: 'lg' }],
};

export const UTM_OVERVIEW: WidgetPanel = {
  id: 'utm-overview',
  label: 'UTM',
  node: null,
  widgets: [{ widgetId: 'utm.status', format: 'lg' }],
};
export const UTM_NAT: WidgetPanel = {
  id: 'utm-nat',
  label: 'NAT',
  node: null,
  widgets: [{ widgetId: 'utm.nat', format: 'lg' }],
};
export const UTM_RULES: WidgetPanel = {
  id: 'utm-rules',
  label: 'Rules',
  node: null,
  widgets: [{ widgetId: 'utm.rules', format: 'lg' }],
};
export const UTM_ALIASES: WidgetPanel = {
  id: 'utm-aliases',
  label: 'Aliases',
  node: null,
  widgets: [{ widgetId: 'utm.aliases', format: 'lg' }],
};
export const UTM_ROUTES: WidgetPanel = {
  id: 'utm-routes',
  label: 'Routes',
  node: null,
  widgets: [{ widgetId: 'utm.route', format: 'lg' }],
};
export const UTM_INTERFACES: WidgetPanel = {
  id: 'utm-interfaces',
  label: 'Interfaces',
  node: null,
  widgets: [{ widgetId: 'utm.interfaces', format: 'lg' }],
};
export const UTM_NET: WidgetPanel = {
  id: 'utm-net',
  label: 'Net',
  node: null,
  widgets: [{ widgetId: 'utm.net', format: 'lg' }],
};
export const UTM_INSTANCE: WidgetPanel = {
  id: 'utm-instance',
  label: 'Instance',
  node: null,
  widgets: [{ widgetId: 'utm.instance', format: 'lg' }],
};

export const ADDONSTORE_BROWSE: WidgetPanel = {
  id: 'addonstore-browse',
  label: i18n.t('addonStore.tabBrowse', 'Přehled'),
  node: null,
  widgets: [{ widgetId: 'addonstore.browse', format: 'lg' }],
};
export const ADDONSTORE_INSTALLED: WidgetPanel = {
  id: 'addonstore-installed',
  label: i18n.t('addonStore.tabInstalled', 'Nainstalované'),
  node: null,
  widgets: [{ widgetId: 'addonstore.installed', format: 'lg' }],
};
export const ADDONSTORE_AVAILABLE: WidgetPanel = {
  id: 'addonstore-available',
  label: i18n.t('addonStore.tabAvailable', 'Dostupné'),
  node: null,
  widgets: [{ widgetId: 'addonstore.available', format: 'lg' }],
};
export const ADDONSTORE_HA: WidgetPanel = {
  id: 'addonstore-ha',
  label: i18n.t('addonStore.haAddonsTab', 'HA Addons'),
  node: null,
  widgets: [{ widgetId: 'addonstore.ha', format: 'lg' }],
};

export const DOCKER_CONTAINERS: WidgetPanel = {
  id: 'docker-containers',
  label: i18n.t('containers.tabContainers', 'Kontejnery'),
  node: null,
  widgets: [
    { widgetId: 'docker.containers', format: 'lg' },
  ],
};

export const DOCKER_IMAGES: WidgetPanel = {
  id: 'docker-images',
  label: 'Images',
  node: null,
  widgets: [
    { widgetId: 'docker.images', format: 'lg' },
  ],
};

export const DOCKER_VOLUMES: WidgetPanel = {
  id: 'docker-volumes',
  label: 'Volumes',
  node: null,
  widgets: [
    { widgetId: 'docker.volumes', format: 'lg' },
  ],
};

export const DOCKER_NETWORKS: WidgetPanel = {
  id: 'docker-networks',
  label: i18n.t('containers.tabNetworks', 'Sítě'),
  node: null,
  widgets: [
    { widgetId: 'docker.networks', format: 'lg' },
  ],
};

// Supabase 2.1 — widget-first (instances / schemata / ACL users).
export const SUPABASE_INSTANCES: WidgetPanel = {
  id: 'supabase-instances',
  label: 'Instance',
  node: null,
  widgets: [{ widgetId: 'supabase.instances', format: 'lg' }],
};

export const SUPABASE_SCHEMATA: WidgetPanel = {
  id: 'supabase-schemata',
  label: 'Schemata',
  node: null,
  widgets: [{ widgetId: 'supabase.schemata', format: 'lg' }],
};

export const SUPABASE_USERS: WidgetPanel = {
  id: 'supabase-users',
  label: 'Users / ACL',
  node: null,
  widgets: [{ widgetId: 'supabase.users', format: 'lg' }],
};

// AppHost 2.1 — instances (+ detail) a Lovable imports
export const APPHOST_INSTANCES: WidgetPanel = {
  id: 'apphost-instances',
  label: 'Instance',
  node: null,
  widgets: [{ widgetId: 'apphost.instances', format: 'lg' }],
};

export const APPHOST_LOVABLE: WidgetPanel = {
  id: 'apphost-lovable',
  label: 'Lovable',
  node: null,
  widgets: [{ widgetId: 'apphost.lovable', format: 'lg' }],
};

// NPM Proxy Manager 2.1
export const NPM_HOSTS: WidgetPanel = {
  id: 'npm-hosts',
  label: 'HTTP hosts',
  node: null,
  widgets: [{ widgetId: 'npm.hosts', format: 'lg' }],
};
export const NPM_STREAM: WidgetPanel = {
  id: 'npm-stream',
  label: 'Stream',
  node: null,
  widgets: [{ widgetId: 'npm.stream', format: 'lg' }],
};
export const NPM_TTY: WidgetPanel = {
  id: 'npm-tty',
  label: 'TTY',
  node: null,
  widgets: [{ widgetId: 'npm.tty', format: 'lg' }],
};
export const NPM_CERTS: WidgetPanel = {
  id: 'npm-certs',
  label: 'Certs',
  node: null,
  widgets: [{ widgetId: 'npm.certs', format: 'lg' }],
};
export const NPM_TEMPLATES: WidgetPanel = {
  id: 'npm-templates',
  label: 'Templates',
  node: null,
  widgets: [{ widgetId: 'npm.templates', format: 'lg' }],
};
export const NPM_EDGE: WidgetPanel = {
  id: 'npm-edge',
  label: 'Edge',
  node: null,
  widgets: [{ widgetId: 'npm.edge', format: 'lg' }],
};
export const NPM_SETTINGS: WidgetPanel = {
  id: 'npm-settings',
  label: 'Settings',
  node: null,
  widgets: [{ widgetId: 'npm.settings', format: 'lg' }],
};

// Tasks — Task Manager 2.1 (mine / system / module / project / runs / events)
export const TASKS_USER: WidgetPanel = {
  id: 'tasks-user',
  label: 'My tasks',
  node: null,
  widgets: [{ widgetId: 'tasks.user', format: 'lg' }],
};
export const TASKS_SYSTEM: WidgetPanel = {
  id: 'tasks-system',
  label: 'System tasks',
  node: null,
  widgets: [{ widgetId: 'tasks.system', format: 'lg' }],
};
export const TASKS_MODULES: WidgetPanel = {
  id: 'tasks-modules',
  label: 'Module tasks',
  node: null,
  widgets: [{ widgetId: 'tasks.modules', format: 'lg' }],
};
export const TASKS_PROJECTS: WidgetPanel = {
  id: 'tasks-projects',
  label: 'Project tasks',
  node: null,
  widgets: [{ widgetId: 'tasks.projects', format: 'lg' }],
};
export const TASKS_RUNS: WidgetPanel = {
  id: 'tasks-runs',
  label: 'Runs',
  node: null,
  widgets: [{ widgetId: 'tasks.runs', format: 'lg' }],
};
export const TASKS_EVENTS: WidgetPanel = {
  id: 'tasks-events',
  label: 'Events',
  node: null,
  widgets: [{ widgetId: 'tasks.events', format: 'lg' }],
};

// Mesh / Infrastruktura 2.1 — multi-widget (stejný WidgetPanel chrome jako dashboard)
//
// Proč dřív 2× nody: panel měl současně
//   mesh.topology → MeshNodesGlance (tenčí list-řádky: IP/role/latency)
//   mesh.fleet    → MeshPeersPanel / NodeCard (primární karty: disk/RAM/verze/URL/spawn)
// Stejné nody 2×. Primární jsou NodeCard; glance zůstává pro dashboard, ne tab Uzly.
export const MESH_FLEET: WidgetPanel = {
  id: 'mesh-fleet',
  label: i18n.t('mesh.pageTitle', 'Infrastruktura'),
  node: null,
  widgets: [
    { widgetId: 'mesh.role', format: 'sm' },
    // Primární karty uzlů (NodeCard + spawn/add) — ne glance list
    { widgetId: 'mesh.fleet', format: 'lg' },
  ],
};
export const MESH_RESOURCES: WidgetPanel = {
  id: 'mesh-resources',
  label: 'Registry',
  node: null,
  widgets: [{ widgetId: 'mesh.resources', format: 'lg' }],
};
export const MESH_HEALTH: WidgetPanel = {
  id: 'mesh-health',
  label: 'Health',
  node: null,
  widgets: [{ widgetId: 'mesh.health', format: 'lg' }],
};
export const MESH_ROUTING: WidgetPanel = {
  id: 'mesh-routing',
  label: i18n.t('mesh.tabRouting', 'Routing a quorum'),
  node: null,
  widgets: [{ widgetId: 'mesh.routing', format: 'lg' }],
};
export const MESH_ENROLLMENT: WidgetPanel = {
  id: 'mesh-enrollment',
  label: i18n.t('mesh.tabEnrollment', 'Přidat uzel'),
  node: null,
  widgets: [{ widgetId: 'mesh.enrollment', format: 'lg' }],
};
export const MESH_DISCOVERED: WidgetPanel = {
  id: 'mesh-discovered',
  label: 'Discovered',
  node: null,
  widgets: [{ widgetId: 'mesh.discovered', format: 'lg' }],
};
export const MESH_BOOTSTRAP: WidgetPanel = {
  id: 'mesh-bootstrap',
  label: 'Bootstrap',
  node: null,
  widgets: [{ widgetId: 'mesh.bootstrap', format: 'lg' }],
};
export const MESH_SECURITY: WidgetPanel = {
  id: 'mesh-security',
  label: 'Security',
  node: null,
  widgets: [{ widgetId: 'mesh.security', format: 'lg' }],
};
export const MESH_TENANTS: WidgetPanel = {
  id: 'mesh-tenants',
  label: i18n.t('mesh.tabTenants', 'Tenanti'),
  node: null,
  widgets: [{ widgetId: 'mesh.tenants', format: 'lg' }],
};
export const MESH_TRUST: WidgetPanel = {
  id: 'mesh-trust',
  label: 'Trust',
  node: null,
  widgets: [{ widgetId: 'mesh.trust', format: 'lg' }],
};

export const RUSTDESK_STATIONS: WidgetPanel = {
  id: 'rustdesk-stations',
  label: i18n.t('rustdesk.tabStations', 'Stanice'),
  node: null,
  widgets: [{ widgetId: 'rustdesk.stations', format: 'lg' }],
};
export const RUSTDESK_BOOKS: WidgetPanel = {
  id: 'rustdesk-books',
  label: i18n.t('rustdesk.tabBooks', 'Adresáře'),
  node: null,
  widgets: [{ widgetId: 'rustdesk.books', format: 'lg' }],
};
export const RUSTDESK_PACKAGES: WidgetPanel = {
  id: 'rustdesk-packages',
  label: i18n.t('rustdesk.tabPackages', 'Balíčky'),
  node: null,
  widgets: [{ widgetId: 'rustdesk.packages', format: 'lg' }],
};
export const RUSTDESK_AUDIT: WidgetPanel = {
  id: 'rustdesk-audit',
  label: i18n.t('rustdesk.tabAudit', 'Audit'),
  node: null,
  widgets: [{ widgetId: 'rustdesk.audit', format: 'lg' }],
};
export const RUSTDESK_SERVER: WidgetPanel = {
  id: 'rustdesk-server',
  label: i18n.t('rustdesk.tabServer', 'Server'),
  node: null,
  widgets: [
    { widgetId: 'rustdesk.engine', format: 'lg' },
    { widgetId: 'rustdesk.public', format: 'lg' },
    { widgetId: 'rustdesk.login', format: 'md' },
    { widgetId: 'rustdesk.fleet', format: 'lg' },
  ],
};
export const RUSTDESK_CONTAINERS: WidgetPanel = {
  id: 'rustdesk-containers',
  label: i18n.t('rustdesk.tabContainers', 'Kontejnery'),
  node: null,
  widgets: [{ widgetId: 'rustdesk.containers', format: 'lg' }],
};
